import * as React from 'react';
import { Box, Paper, styled } from '@mui/material';
import color from 'color';
import { EAwardsScreenShape, IAwardVisualization, IPointAward } from 'components/awards/models';
import { ELoyaltyTemplate } from 'components/awards/enums';

const PREFIX = 'AwardVisualizer';

const classes = {
    container: `${PREFIX}container`,
    overlay: `${PREFIX}overlay`
};

const getStylesPerShape = (shapeType?: 'small' | 'large' | 'rectangle') => {
    switch (shapeType) {
        case 'small':
            return {
                maxWidth: '33.333333%',
                flexGrow: 0,
                flexBasis: '33.333333%'
            };
        case 'large':
            return {
                maxWidth: '50%',
                flexGrow: 0,
                flexBasis: '50%'
            };
        case 'rectangle':
            return {
                maxWidth: '100%',
                flexGrow: 0,
                flexBasis: '100%'
            };
        default:
            return {};
    }
};

type StyledPaperProps = IAwardVisualization & { shapeType?: 'small' | 'large' | 'rectangle' };

type TextAlign = 'left' | 'center' | 'right';

const StyledPaper = styled(Paper, {
    shouldForwardProp: (prop: string) =>
        ![
            'backgroundOverlayColour',
            'backgroundOverlayOpacity',
            'backgroundImageUrl',
            'textColour',
            'awardsScreenShape',
            'shapeType'
        ].includes(prop)
})<StyledPaperProps>(
    ({
        theme,
        backgroundOverlayColour,
        backgroundOverlayOpacity,
        backgroundImageUrl,
        textColour,
        awardsScreenShape,
        shapeType
    }) => {
        const isValidBackground = /^#([0-9A-F]{3}){1,2}$/i.test(backgroundOverlayColour || '');
        const padding = getContainerPaddingByShape(awardsScreenShape);

        return {
            width: '100%',
            padding: theme.spacing(0, 0.5),
            ...getStylesPerShape(shapeType),
            [`& .${classes.container}`]: {
                position: 'relative',
                borderRadius: theme.shape.borderRadius,
                backgroundImage: backgroundImageUrl ? `url("${backgroundImageUrl}")` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center center',
                overflow: 'hidden',
                color: textColour,
                width: '100%',
                paddingBottom: padding
            },
            [`& .${classes.overlay}`]: {
                position: 'absolute',
                backgroundColor: isValidBackground
                    ? color(backgroundOverlayColour)
                          .alpha(backgroundOverlayOpacity || 0)
                          .rgb()
                          .string()
                    : 'initial',
                width: '100%',
                height: '100%',
                fontSize: getFontSizeByShape(awardsScreenShape),
                padding: theme.spacing(getOverlayPaddingByShape(awardsScreenShape))
            }
        };
    }
);

interface IProps {
    visualization: IAwardVisualization;
    ignoreAlignment: boolean;
    onClick: (award: IPointAward) => void;
    award: IPointAward;
}

const getFontSizeByShape = (awardsScreenShape?: EAwardsScreenShape) => {
    switch (awardsScreenShape) {
        case EAwardsScreenShape.LARGE_SQUARE:
            return 14;
        case EAwardsScreenShape.SMALL_SQUARE:
            return 8;
        default:
            return undefined;
    }
};

const getGridItemSizeByShape = (awardsScreenShape?: EAwardsScreenShape): 'small' | 'large' | 'rectangle' => {
    switch (awardsScreenShape) {
        case EAwardsScreenShape.LARGE_SQUARE:
            return 'large';
        case EAwardsScreenShape.SMALL_SQUARE:
            return 'small';
        default:
            return 'rectangle';
    }
};

const getOverlayPaddingByShape = (awardsScreenShape?: EAwardsScreenShape) => {
    switch (awardsScreenShape) {
        case EAwardsScreenShape.LARGE_SQUARE:
            return 1.5;
        case EAwardsScreenShape.SMALL_SQUARE:
            return 1;
        case EAwardsScreenShape.SINGLE_RECTANGLE:
        case EAwardsScreenShape.DOUBLE_RECTANGLE:
        default:
            return 2;
    }
};

const getContainerPaddingByShape = (awardsScreenShape?: EAwardsScreenShape) => {
    switch (awardsScreenShape) {
        case EAwardsScreenShape.LARGE_SQUARE:
        case EAwardsScreenShape.SMALL_SQUARE:
            return '100%';
        case EAwardsScreenShape.SINGLE_RECTANGLE:
            return `${100 / 3}%`;
        case EAwardsScreenShape.DOUBLE_RECTANGLE:
        default:
            return `${(100 / 3) * 2}%`;
    }
};

export const AwardVisualizer: React.FC<React.PropsWithChildren<IProps>> = React.memo(
    ({ children, visualization, ignoreAlignment, onClick, award }) => {
        const awardVisualization = React.useMemo(
            () => ({
                ...visualization,
                awardsScreenShape:
                    award.template === ELoyaltyTemplate.AD_HOC || award.template === ELoyaltyTemplate.CLUB
                        ? visualization.awardsScreenShape
                        : undefined
            }),
            [visualization, award]
        );
        const alignItems = React.useMemo(() => {
            if (!ignoreAlignment) {
                switch (visualization.verticalAlignment) {
                    case 'CENTER':
                        return 'center';
                    case 'BOTTOM':
                        return 'flex-end';
                    default:
                        return 'flex-start';
                }
            }
            return 'flex-start';
        }, [ignoreAlignment, visualization.verticalAlignment]);
        const handleClick = React.useCallback(() => onClick(award), [award, onClick]);

        const shapeType = React.useMemo(
            () =>
                getGridItemSizeByShape(
                    award?.template === ELoyaltyTemplate.AD_HOC || award?.template === ELoyaltyTemplate.CLUB
                        ? visualization.awardsScreenShape
                        : undefined
                ),
            [award?.template, visualization.awardsScreenShape]
        );

        const textAlignment = React.useMemo(
            () => visualization.textAlignment?.toLowerCase() as TextAlign | undefined,
            [visualization.textAlignment]
        );

        return (
            <StyledPaper elevation={0} onClick={handleClick} shapeType={shapeType} {...awardVisualization}>
                <div className={classes.container}>
                    <Box
                        display="flex"
                        alignItems={alignItems}
                        textAlign={textAlignment}
                        className={classes.overlay}
                    >
                        {children}
                    </Box>
                </div>
            </StyledPaper>
        );
    },
    (prevProps, nextProps) => prevProps.onClick === nextProps.onClick
);

AwardVisualizer.displayName = 'AwardVisualizer';
